<script setup lang="ts">
import { computed, watch } from 'vue'
import { useUiBottomSheetStore } from '@/stores/uiBottomSheet'

const bottomSheetStore = useUiBottomSheetStore()

const isOpen = computed(() => bottomSheetStore.isOpen)
const config = computed(() => bottomSheetStore.openedConfig)

// When bottom sheet is open - disallow scrolling.
watch(
  () => isOpen.value,
  (isOpen) => {
    window.document.body.style.overflowY = isOpen ? 'hidden' : 'auto'
  },
  { immediate: true }
)

const onClose = () => bottomSheetStore.close()
</script>

<template>
  <!-- Background with transition -->
  <div class="relative h-[0]">
    <Transition name="fade">
      <div
        v-if="isOpen"
        data-testid="bottomSheetBackground"
        class="fixed inset-0 bg-black bg-opacity-70 z-50"
        @click="onClose()"
      ></div>
    </Transition>

    <!-- Content with transition -->
    <Transition name="slide-up">
      <div
        v-if="isOpen"
        data-testid="bottomSheetContent"
        class="fixed bottom-0 min-h-[200px] w-full bg-[white] max-w-md mx-auto border rounded-t-[24px] p-[24px] pb-[48px] gap-[24px] flex flex-col z-50 left-0 right-0"
        @click.stop
      >
        <Component v-if="config?.content" :is="config?.content(onClose)" />
        <template v-else>A bottom sheet</template>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
/* Fade transition for background */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide-up transition for content */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}
</style>
